/**
 * plural.ts — picks the grammatical-number form for a count.
 *
 * Why this exists instead of i18next's own `_one` / `_few` / `_many` suffixes:
 * Polish needs three forms (1 sesja · 2–4 sesje · 5+ sesji) where English needs
 * two, and the i18nKeys test enforces an identical key set in both locales. Two
 * locales with different suffix sets would fail that parity check. So every
 * count-bearing string carries the same three sub-keys, `one`, `few`, `many`,
 * in both locales, and English simply repeats its plural in `few` and `many`.
 *
 * Usage:
 *   t(`home.sessionsLabel.${pluralCategory(n, i18n.language)}`, { count: n })
 *
 * The category comes from Intl.PluralRules, so the Polish rule (2–4 → few,
 * except 12–14 → many) is the browser's, not hand-rolled.
 */
export type PluralCategory = 'one' | 'few' | 'many'

export function pluralCategory(count: number, lang: string | undefined): PluralCategory {
  let category: string
  try {
    category = new Intl.PluralRules(lang || 'en').select(count)
  } catch {
    category = count === 1 ? 'one' : 'other'
  }
  if (category === 'one') return 'one'
  if (category === 'two' || category === 'few') return 'few'
  return 'many'
}
